import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { CheckCircle, AlertCircle, ExternalLink, Key, Settings } from 'lucide-react';
import BingConnectionTooltip from '../../../components/seo/BingConnectionTooltip';

const IntegrationsTab: React.FC = () => {
  const { user } = useAuth();
  const [googleConnected, setGoogleConnected] = useState(false);
  const [bingConnected, setBingConnected] = useState(false);
  const [googleSites, setGoogleSites] = useState<any[]>([]);
  const [bingSites, setBingSites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkIntegrations();
  }, [user]);

  const checkIntegrations = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);

      try {
        const googleRes = await fetch(`/api/marketing/google/sites?userId=${user.id}`);
        if (googleRes.ok) {
          const data = await googleRes.json();
          setGoogleConnected(true);
          setGoogleSites(data.sites || []);
        }
      } catch (err) {
        setGoogleConnected(false);
      }

      try {
        const bingRes = await fetch(`/api/marketing/bing/sites?userId=${user.id}`);
        if (bingRes.ok) {
          const data = await bingRes.json();
          setBingConnected(true);
          setBingSites(data.sites || []);
        }
      } catch (err) {
        setBingConnected(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const response = await fetch('/api/marketing/google/auth-url');
      const { authUrl } = await response.json();
      
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      window.open(
        authUrl,
        'Connect Google Search Console',
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } catch (error) {
      console.error('Error connecting Google:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-6 h-6 text-green-600" />
          Integrations
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Connect your Google Search Console and Bing Webmaster Tools to get real SEO data
        </p>
      </div>

      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Google Search Console</h3>
                <p className="text-sm text-gray-600">OAuth 2.0 • Real ranking data</p>
              </div>
            </div>
            
            {googleConnected ? (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Connected</span>
              </div>
            ) : (
              <button
                onClick={handleConnectGoogle}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Key className="w-4 h-4" />
                Connect Google
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          {googleConnected && googleSites.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700 uppercase tracking-wider">Your Sites:</p>
              {googleSites.slice(0, 5).map((site, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 truncate">{site.siteUrl || site}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center">
                <span className="text-2xl">B</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Bing Webmaster Tools</h3>
                <p className="text-sm text-gray-600">API Key • Search volume data</p>
              </div>
            </div>
            
            {bingConnected ? (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Connected</span>
              </div>
            ) : (
              <BingConnectionTooltip 
                userId={user?.id || ''}
                onSuccess={() => checkIntegrations()}
              />
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          {bingConnected && bingSites.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700 uppercase tracking-wider">Your Sites:</p>
              {bingSites.slice(0, 5).map((site, idx) => (
                <div key={idx} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 truncate">{site.siteUrl}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IntegrationsTab;
